import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { Camera, Check, Clock, Film, GripVertical, Info, Pencil, Trash2, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import {
    CameraAngles,
    CameraMovements,
    FieldHelpers,
    Framings,
    ShotPriorities,
    ShotTypes
} from '../constants/shot-constants'
import { Shot, useShotListStore } from '../store/useShotListStore'

interface EditableShotCardProps {
    shot: Shot
    sceneId: string
    index: number
    defaultEditing?: boolean
}

export const EditableShotCard: React.FC<EditableShotCardProps> = ({ shot, sceneId, index, defaultEditing = false }) => {
    const { updateShot, removeShot } = useShotListStore()
    const [localShot, setLocalShot] = useState<Shot>(shot)
    const [isEditing, setIsEditing] = useState(defaultEditing)

    useEffect(() => {
        setLocalShot(shot)
    }, [shot])

    const handleChange = (field: keyof Shot, value: any) => {
        const updated = { ...localShot, [field]: value }
        setLocalShot(updated)
    }

    const handleSave = () => {
        updateShot(sceneId, shot.id, localShot)
        setIsEditing(false)
    }

    const handleCancel = () => {
        setLocalShot(shot)
        setIsEditing(false)
    }

    // Helper for rendering form fields with tooltips
    const renderFieldWithTooltip = (label: string, helperText: string, children: React.ReactNode) => (
        <div className="space-y-1">
            <div className="flex items-center gap-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</label>
                <TooltipProvider>
                    <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-muted-foreground/50 cursor-help hover:text-primary transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[200px] text-xs">
                            {helperText}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            {children}
        </div>
    )

    if (isEditing) {
        return (
            <Card className="overflow-hidden border-primary/50 shadow-lg relative p-4 bg-muted/20">
                <div className="flex items-center justify-between border-b pb-2 mb-4">
                    <Badge variant="secondary" className="font-mono">
                        Shot #{index + 1} (Editing)
                    </Badge>
                    <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={handleCancel}>
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                        </Button>
                        <Button size="sm" onClick={handleSave}>
                            <Check className="h-4 w-4 mr-1" />
                            Save Changes
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    {renderFieldWithTooltip("Subject", FieldHelpers.subject, (
                        <Input
                            value={localShot.subject || ''}
                            onChange={(e) => handleChange('subject', e.target.value)}
                            placeholder="Subject (e.g., John)"
                            className="h-8 font-semibold w-full"
                        />
                    ))}
                    {renderFieldWithTooltip("Priority", FieldHelpers.shotPriority, (
                        <Select value={localShot.shotPriority || ''} onValueChange={(val) => handleChange('shotPriority', val)}>
                            <SelectTrigger className="h-8 w-full"><SelectValue placeholder="Priority" /></SelectTrigger>
                            <SelectContent>
                                {ShotPriorities.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    ))}
                </div>

                <div className="space-y-4">
                    {renderFieldWithTooltip("Description", FieldHelpers.description, (
                        <Textarea
                            value={localShot.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Description..."
                            className="min-h-[60px] resize-none text-sm"
                            rows={2}
                        />
                    ))}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {renderFieldWithTooltip("Shot", FieldHelpers.shotType, (
                            <Select value={localShot.shotType} onValueChange={(val) => handleChange('shotType', val)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Size" /></SelectTrigger>
                                <SelectContent>{ShotTypes.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                            </Select>
                        ))}
                        {renderFieldWithTooltip("Angle", FieldHelpers.cameraAngle, (
                            <Select value={localShot.cameraAngle || ''} onValueChange={(val) => handleChange('cameraAngle', val)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Angle" /></SelectTrigger>
                                <SelectContent>{CameraAngles.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                            </Select>
                        ))}
                        {renderFieldWithTooltip("Movement", FieldHelpers.cameraMovement, (
                            <Select value={localShot.cameraMovement} onValueChange={(val) => handleChange('cameraMovement', val)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Move" /></SelectTrigger>
                                <SelectContent>{CameraMovements.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                            </Select>
                        ))}
                        {renderFieldWithTooltip("Framing", FieldHelpers.framing, (
                            <Select value={localShot.framing || ''} onValueChange={(val) => handleChange('framing', val)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Frame" /></SelectTrigger>
                                <SelectContent>{Framings.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
                            </Select>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                        {renderFieldWithTooltip("Emotional Purpose", FieldHelpers.emotionalPurpose, (
                            <Input
                                value={localShot.emotionalPurpose || ''}
                                onChange={(e) => handleChange('emotionalPurpose', e.target.value)}
                                placeholder="Emotional Purpose"
                                className="h-8 text-xs"
                            />
                        ))}
                        {renderFieldWithTooltip("Duration", FieldHelpers.duration, (
                            <Input
                                value={localShot.duration || ''}
                                onChange={(e) => handleChange('duration', e.target.value)}
                                placeholder="Duration"
                                className="h-8 text-xs"
                            />
                        ))}
                    </div>
                </div>
            </Card>
        )
    }

    return (
        <Card className="overflow-hidden hover:shadow-lg transition-all group border-border/50 hover:border-primary/30 relative">
            <div className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 cursor-grab text-muted-foreground transition-opacity z-10">
                <GripVertical className="h-5 w-5" />
            </div>

            <div className="flex flex-col h-full pl-6">
                <div className="flex-1 p-5 flex flex-col justify-between">
                    <div className="space-y-4">
                        <div className="flex justify-between items-start gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-mono text-muted-foreground font-bold">#{index + 1}</span>
                                <h3 className="font-semibold text-lg leading-tight line-clamp-2" title={shot.subject}>
                                    {shot.subject || <span className="text-muted-foreground italic">No Subject</span>}
                                </h3>
                                {shot.shotPriority && (
                                    <Badge variant={shot.shotPriority === 'Must Have' ? 'default' : 'secondary'} className="text-[10px] h-5 pointer-events-none">
                                        {shot.shotPriority}
                                    </Badge>
                                )}
                            </div>
                            {shot.duration && (
                                <Badge variant="outline" className="flex-shrink-0 gap-1.5 h-7">
                                    <Clock className="w-3.5 h-3.5" />
                                    {shot.duration}
                                </Badge>
                            )}
                        </div>

                        <p className="text-sm text-muted-foreground leading-relaxed min-h-[1.25rem]">
                            {shot.description || <span className="italic">No Description</span>}
                        </p>

                        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                            {shot.shotType && (
                                <Badge variant="secondary" className="font-normal text-[10px]">{shot.shotType}</Badge>
                            )}
                            {shot.framing && (
                                <Badge variant="secondary" className="font-normal text-[10px]">{shot.framing}</Badge>
                            )}
                            {shot.cameraAngle && (
                                <div className="flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-md text-xs">
                                    <Camera className="w-3.5 h-3.5" />
                                    <span className="font-medium">Angle:</span> {shot.cameraAngle}
                                </div>
                            )}
                            {shot.cameraMovement && (
                                <div className="flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-md text-xs">
                                    <Film className="w-3.5 h-3.5" />
                                    <span className="font-medium">Move:</span> {shot.cameraMovement}
                                </div>
                            )}
                        </div>

                        {shot.emotionalPurpose && (
                            <div className="text-xs text-muted-foreground italic border-t border-border/40 pt-2 mt-2">
                                "{shot.emotionalPurpose}"
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border/40">
                        <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit Text
                        </Button>

                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            onClick={() => removeShot(sceneId, shot.id)}
                            title="Remove Shot"
                        >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                        </Button>
                    </div>
                </div>
            </div>
        </Card>
    )
}
